import { useLayoutEffect, useRef } from "react";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const isTestEnvironment =
  typeof navigator !== "undefined" &&
  navigator.userAgent.toLowerCase().includes("jsdom");

if (!isTestEnvironment) gsap.registerPlugin(ScrollTrigger);

export function usePortfolioMotion(rootRef, introComplete) {
  const lenisRef = useRef(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !introComplete || isTestEnvironment) return undefined;

    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let lenis;
    let ticker;

    if (!reduceMotion) {
      lenis = new Lenis({
        duration: 1.15,
        smoothWheel: true,
        syncTouch: false,
        anchors: true,
      });
      lenisRef.current = lenis;
      lenis.on("scroll", ScrollTrigger.update);
      ticker = (time) => lenis.raf(time * 1000);
      gsap.ticker.add(ticker);
      gsap.ticker.lagSmoothing(0);
    }

    const context = gsap.context(() => {
      ScrollTrigger.create({
        start: 0,
        end: "max",
        onUpdate: (self) => {
          const progress = Math.round(self.progress * 100);
          document.documentElement.style.setProperty("--scroll-progress", self.progress.toFixed(4));
          const progressNumber = document.querySelector(".edge-progress__number");
          if (progressNumber) progressNumber.textContent = String(progress).padStart(2, "0");
        },
      });

      if (reduceMotion) return;

      gsap.utils.toArray("[data-scroll-mask]").forEach((element) => {
        gsap.from(element.querySelector(".mask-reveal__inner"), {
          yPercent: 112,
          skewY: 3,
          duration: 0.9,
          ease: "power4.out",
          scrollTrigger: {
            trigger: element,
            start: "top 88%",
            toggleActions: "play reverse play reverse",
          },
        });
      });

      gsap.utils.toArray("[data-reveal='fade']").forEach((element) => {
        gsap.from(element, {
          y: 30,
          opacity: 0,
          duration: 0.75,
          ease: "power3.out",
          scrollTrigger: {
            trigger: element,
            start: "top 88%",
            toggleActions: "play reverse play reverse",
          },
        });
      });

      gsap.utils.toArray("[data-line]").forEach((line) => {
        gsap.from(line, {
          scaleX: 0,
          transformOrigin: "left center",
          duration: 0.9,
          ease: "power3.inOut",
          scrollTrigger: {
            trigger: line,
            start: "top 90%",
            toggleActions: "play reverse play reverse",
          },
        });
      });

      gsap.utils.toArray(".stack-row").forEach((row) => {
        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: row,
            start: "top 86%",
            toggleActions: "play reverse play reverse",
          },
        });
        timeline
          .from(row.querySelector("[data-stack-line]"), { scaleX: 0, transformOrigin: "left center", duration: 0.7, ease: "power3.inOut" })
          .from(row.querySelector(".stack-row__heading"), { y: 18, opacity: 0, duration: 0.5, ease: "power3.out" }, "-=0.35")
          .from(row.querySelectorAll("[data-stack-skill]"), { y: 16, opacity: 0, duration: 0.42, stagger: 0.06, ease: "power3.out" }, "-=0.3");

        ScrollTrigger.create({
          trigger: row,
          start: "top 55%",
          end: "bottom 45%",
          onToggle: (self) => row.classList.toggle("is-focused", self.isActive),
        });
      });

      gsap.utils.toArray(".experience-row").forEach((row) => {
        const title = row.querySelector("[data-experience-title] .mask-reveal__inner");
        gsap.timeline({
          scrollTrigger: {
            trigger: row,
            start: "top 86%",
            toggleActions: "play reverse play reverse",
          },
        })
          .from(row.querySelector("[data-experience-line]"), { scaleX: 0, transformOrigin: "left center", duration: 0.7, ease: "power3.inOut" })
          .from(title, { yPercent: 110, skewY: 3, duration: 0.68, ease: "power4.out" }, "-=0.3")
          .from(row.querySelectorAll("small, .experience-row__main > span, .experience-row__meta"), { y: 12, opacity: 0, duration: 0.45, stagger: 0.05 }, "-=0.45");

        ScrollTrigger.create({
          trigger: row,
          start: "top 50%",
          end: "bottom 50%",
          onToggle: (self) => row.classList.toggle("is-focused", self.isActive),
        });
      });

      gsap.utils.toArray(".project-row").forEach((row) => {
        gsap.from(row, {
          y: 22,
          opacity: 0,
          duration: 0.62,
          ease: "power3.out",
          scrollTrigger: {
            trigger: row,
            start: "top 90%",
            toggleActions: "play reverse play reverse",
          },
        });
      });

      const fadeTargets = gsap.utils.toArray([
        ".hero__content",
        ".metrics-band__inner",
        ".about__statement",
        ".about__details",
        ".stack-row",
        ".experience-row",
        ".projects__layout",
        ".footer__contact",
      ].join(", "));

      fadeTargets.forEach((element) => {
        gsap.timeline({
          scrollTrigger: {
            trigger: element,
            start: "top 96%",
            end: "bottom 4%",
            scrub: 0.35,
          },
        })
          .fromTo(element, { opacity: 0 }, { opacity: 1, duration: 0.16, ease: "none" })
          .to(element, { opacity: 1, duration: 0.68, ease: "none" })
          .to(element, { opacity: 0, duration: 0.16, ease: "none" });
      });

    }, root);

    ScrollTrigger.refresh();

    return () => {
      context.revert();
      if (ticker) gsap.ticker.remove(ticker);
      lenis?.destroy();
      lenisRef.current = null;
      document.documentElement.style.removeProperty("--scroll-progress");
    };
  }, [introComplete, rootRef]);

  return lenisRef;
}
